import { Button, Checkbox, Modal } from "flowbite-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { HiPlus } from "react-icons/hi";
import { useAppSelector } from "../../Reducers/store";
import Loader from "../Common/Loader";
import JobModal from "./JobModal";
import JobApi from "../../Api/JobApi";
import { Job } from "../../Models/Job";
import JobListTable from "./JobListTable";

export default function JobList() {
  const { userModel } = useAppSelector((state) => state.user);
  const [jobListData, setjobListData] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  // NEW: toggle for active jobs only
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(true);
  // NEW: toggle for showing deleted jobs
  const [showDeletedJobs, setShowDeletedJobs] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [jobToDelete, setJobToDelete] = useState<number | null>(null);

  const getJobs = useCallback(() => {
    if (userModel) {
      try {
        setIsLoading(true);
        return JobApi.getJobList()
          .then((res) => {
            setjobListData(res.data);
            setIsLoading(false);
          })
          .catch((err) => {
            console.error(err);
            toast.error("Error");
            setIsLoading(false);
          });
      } catch (error) {
        console.error(error);
        toast.error("Error");
        setIsLoading(false);
      }
    }
  }, [userModel]);

  useEffect(() => {
    getJobs();
  }, [getJobs]);

  const handleAddJobClick = () => {
    setSelectedJob(null);
    setShowModal(true);
  };

  const editJob = (job: Job) => {
    setSelectedJob(job);
    setShowModal(true);
  };

  function deleteJob(id: number) {
    setJobToDelete(id);
    setShowDeleteConfirm(true);
  }

  const confirmDeleteJob = () => {
    if (jobToDelete) {
      JobApi.softDeleteJob(jobToDelete)
        .then((res) => {
          if (res.data.success) {
            toast.success("Job deleted successfully");
            getJobs();
          } else {
            toast.error(res.data.message || "Error while deleting item");
          }
        })
        .catch((err) => {
          toast.error("Error: " + err);
        });
    }
    setShowDeleteConfirm(false);
    setJobToDelete(null);
  };

  function checkAccess() {
    return !!userModel;
  }

  // ---------- Filter logic ----------
  const isActiveJob = (j: Job) => {
    // Prefer explicit flag if your API has it
    if (typeof (j as any).isActive === "boolean") return (j as any).isActive;

    const recurring = Boolean((j as any).isRecurring);

    // Normalize "today"
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // No end date: only active if recurring
    if (!j.endDate) return recurring;

    // Parse end date safely
    const end = new Date(j.endDate as any);
    if (isNaN(end.getTime())) {
      // Don't hide rows on parse weirdness — treat as active
      return true;
    }
    end.setHours(0, 0, 0, 0);

    return end >= today;
  };

  const displayJobs = useMemo(() => {
    let filtered = jobListData;

    // Filter out deleted jobs unless explicitly showing them
    if (!showDeletedJobs) {
      filtered = filtered.filter((job) => !job.deleted);
    }

    // Apply active filter if enabled
    if (showActiveOnly) {
      filtered = filtered.filter(isActiveJob);
    }

    return filtered;
  }, [showActiveOnly, showDeletedJobs, jobListData]);
  // ----------------------------------------

  return (
    <>
      <div className="w-full">
        <div className="w-full h-[74px] p-4">
          <div className="mx-1 flex items-end">
            <div className="flex flex-1 items-center gap-4">
              <h1 className="text-gray-900 font-bold text-4xl">
                <div>Jobs</div>
              </h1>
            </div>

            {checkAccess() && (
              <div>
                <Button onClick={handleAddJobClick} className="buttonMain">
                  <HiPlus size={20} />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-1 gap-4 p-4">
          {/* NEW: Active-only toggle + count */}
          <label className="flex gap-2 text-sm">
            <Checkbox
              checked={showActiveOnly}
              onChange={(e) => setShowActiveOnly(e.currentTarget.checked)}
            />
            Active only
          </label>
          {/* NEW: Show deleted jobs toggle */}
          <label className="flex gap-2 text-sm">
            <Checkbox
              checked={showDeletedJobs}
              onChange={(e) => setShowDeletedJobs(e.currentTarget.checked)}
            />
            Show deleted jobs
          </label>
          <div className="text-sm text-gray-500">
            Showing {displayJobs.length} / {jobListData.length}
          </div>
        </div>
        <div className="w-full p-4 h-[calc(100vh-132px)] md:h-[calc(100vh-200px)] overflow-y-auto">
          <div className="relative">
            {isLoading && <Loader />}
            <JobListTable
              data={displayJobs}
              deleteJob={deleteJob}
              editJob={editJob}
            />
          </div>
        </div>
      </div>

      <JobModal
        show={showModal}
        setShow={setShowModal}
        getJobs={getJobs}
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteConfirm}
        size="md"
        popup
        onClose={() => setShowDeleteConfirm(false)}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <div className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200">
              <svg
                className="mx-auto h-14 w-14 text-gray-400 dark:text-gray-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              Are you sure you want to delete this job? This action can be
              undone by contacting support.
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={confirmDeleteJob}>
                Yes, I'm sure
              </Button>
              <Button color="gray" onClick={() => setShowDeleteConfirm(false)}>
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
